import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaBalanceScale, FaShieldAlt } from 'react-icons/fa';
import { BsLightningChargeFill } from 'react-icons/bs';
import './About.css';

// ── Animation variants ────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const stagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.15 } },
};
const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};
const slideRight = {
  hidden: { opacity: 0, x: 50 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

const vp = { once: true, amount: 0.25 };

// ── Data ──────────────────────────────────────────────────
const STATS = [
  { value: '2M+',  label: 'Claims handled',         color: '#1e40af' },
  { value: '98%',  label: 'Success rate',            color: '#16a34a' },
  { value: '$650', label: 'Max compensation',        color: '#1e40af' },
];

const WHY_CARDS = [
  {
    icon: <FaBalanceScale size={24} />,
    title: 'Expert Legal Team',
    desc:  'Our aviation lawyers know every regulation — EU261, UK261, and beyond. We turn complexity into results.',
    accent: '#1e40af',
  },
  {
    icon: <BsLightningChargeFill size={24} />,
    title: 'Lightning Fast',
    desc:  'Most claims are resolved within 30 days. We follow up relentlessly so you never have to chase anyone.',
    accent: '#16a34a',
  },
  {
    icon: <FaShieldAlt size={24} />,
    title: 'Totally Secure',
    desc:  'Bank-grade encryption protects your data. We never share your information with third parties.',
    accent: '#1e40af',
  },
];

// ── Mission illustration (SVG inline) ────────────────────
function MissionIllustration() {
  return (
    <div className="mission-illustration">
      {/* Decorative diamonds */}
      <span className="mission-diamond mission-diamond--1">◆</span>
      <span className="mission-diamond mission-diamond--2">◆</span>

      {/* Top row — two mini cards */}
      <div className="mission-cards-row">
        <motion.div className="mission-mini-card" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
          <div className="mission-mini-card__img mission-mini-card__img--blue">
            <span className="mmc-icon">⏱</span>
          </div>
          <p>Fast payouts</p>
        </motion.div>
        <motion.div className="mission-mini-card" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
          <div className="mission-mini-card__img mission-mini-card__img--green">
            <span className="mmc-icon">🛡</span>
          </div>
          <p>Rights enforced</p>
        </motion.div>
      </div>

      {/* Bottom row — large card */}
      <motion.div className="mission-main-card" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
        <div className="mission-main-card__img">
          <span className="mmc-icon mmc-icon--lg">💳</span>
          <div className="mmc-cards">
            <div className="mcc" style={{ transform: 'rotate(-8deg)', background: '#1e40af' }} />
            <div className="mcc" style={{ transform: 'rotate(-3deg)', background: '#2563eb' }} />
            <div className="mcc" style={{ transform: 'rotate(2deg)',  background: '#3b82f6' }} />
          </div>
        </div>
        <p>Easy compensation claims</p>
      </motion.div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────
export default function About() {
  return (
    <div className="page about-page">

      {/* ── HERO ── */}
      <section className="about-hero">
        <div className="about-hero__overlay" />
        <motion.div
          className="about-hero__content"
          initial="hidden" whileInView="show" viewport={vp} variants={stagger}
        >
          <motion.span className="about-tag" variants={fadeUp}>About AirRefund</motion.span>
          <motion.h1 className="about-hero__title" variants={fadeUp}>
            We fight airlines.<br />
            <span>You get paid.</span>
          </motion.h1>
          <motion.p className="about-hero__sub" variants={fadeUp}>
            Built by travelers who got burned and discovered airlines count on passengers
            not knowing their rights. We built the tool we wish existed, and millions have used it.
          </motion.p>
        </motion.div>
      </section>

      {/* ── STATS ── */}
      <section className="about-stats">
        <motion.div
          className="about-stats__inner"
          initial="hidden" whileInView="show" viewport={vp} variants={stagger}
        >
          {STATS.map(({ value, label, color }) => (
            <motion.div key={label} className="stat-item" variants={fadeUp}>
              <span className="stat-item__value" style={{ color }}>{value}</span>
              <span className="stat-item__label">{label}</span>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── MISSION ── */}
      <section className="about-mission">
        <div className="about-mission__inner">
          <motion.div
            className="about-mission__text"
            initial="hidden" whileInView="show" viewport={vp} variants={slideLeft}
          >
            <span className="about-tag about-tag--dark">Our Mission</span>
            <h2>We solve flight problems</h2>
            <p className="mission-bold">Helping air passengers is what we do.</p>
            <p>
              Air travel disruptions affect millions every year. EU Regulation 261/2004 entitles
              passengers to up to €600, yet less than 5% ever claim it. Airlines count on that silence.
            </p>
            <p>
              That's why we make it so easy to claim up to $650 for delayed and cancelled flights.
              Our platform handles everything, from eligibility checks to chasing airlines, so you
              get the compensation you're legally owed.
            </p>
            <div className="mission-pills">
              <span>EU261 Covered</span>
              <span>UK261 Covered</span>
              <span>No Win, No Fee</span>
            </div>
          </motion.div>

          <motion.div
            initial="hidden" whileInView="show" viewport={vp} variants={slideRight}
          >
            <MissionIllustration />
          </motion.div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section className="about-why">
        <div className="about-why__inner">
          <motion.div
            className="about-why__header"
            initial="hidden" whileInView="show" viewport={vp} variants={fadeUp}
          >
            <span className="about-tag about-tag--dark">Why choose us</span>
            <h2>What makes us <span>different</span></h2>
            <p>We've spent 15 years refining every part of the claims process so you never have to.</p>
          </motion.div>

          <motion.div
            className="why-cards"
            initial="hidden" whileInView="show" viewport={vp} variants={stagger}
          >
            {WHY_CARDS.map(({ icon, title, desc, accent }) => (
              <motion.div
                key={title}
                className="why-card"
                variants={fadeUp}
                whileHover={{ y: -6, boxShadow: '0 20px 48px rgba(10,22,40,0.13)' }}
                transition={{ duration: 0.25 }}
              >
                <div className="why-card__icon-wrap" style={{ background: `${accent}12`, color: accent }}>
                  {icon}
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
                <div className="why-card__bar" style={{ background: accent }} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="about-cta">
        <motion.div
          className="about-cta__inner"
          initial="hidden" whileInView="show" viewport={vp} variants={stagger}
        >
          <div className="about-cta__glow" />
          <motion.div className="about-cta__text" variants={slideLeft}>
            <span className="about-tag about-tag--dark" style={{ marginBottom: 14, display: 'inline-block' }}>
              Ready to claim?
            </span>
            <h2 className="about-cta__title">
              Your compensation is waiting to be claimed
            </h2>
            <p className="about-cta__sub">
              It takes less than 5 minutes. No upfront cost. No legal knowledge needed.
            </p>
          </motion.div>
          <motion.div variants={slideRight}>
            <Link to="/check-compensation" className="about-cta__btn">
              Check My Compensation
            </Link>
          </motion.div>
        </motion.div>
      </section>

    </div>
  );
}